import RequestMethod from "@/models/client/RequestMethod";
import RestResponse from "@/models/client/RestResponse";
import {
  ConsolidationActivationDatesParams,
  ConsolidationActivationDatesResult,
  ConsolidationBlueprintResult,
  ConsolidationPriority,
  ConsolidationPriorityStatusItem,
  ConsolidationRenderByChangeLawParams,
  ConsolidationRenderParams,
  ConsolidationRenderResult,
  ConsolidationContainersParams,
  ConsolidationView,
  ContainerRenderResult,
  ContainerChangeDetail,
  CreateConsolidationPriorityParams,
  GetContainerChangeDetailParams,
  SubmitConsolidationFeedbackParams,
  UpdateConsolidationPriorityParams,
} from "@/models/consolidation";
import { RestClient } from "./restClient";

export type ConsolidationServiceType = {
  render: (
    params: ConsolidationRenderParams,
    signal?: AbortSignal
  ) => RestResponse<ConsolidationRenderResult>;
  renderByChangeLaw: (
    params: ConsolidationRenderByChangeLawParams,
    signal?: AbortSignal
  ) => RestResponse<ConsolidationRenderResult>;
  getContainers: (
    params: ConsolidationContainersParams,
    signal?: AbortSignal
  ) => RestResponse<ContainerRenderResult[]>;
  getBlueprint: (
    lawExternalId: string,
    signal?: AbortSignal
  ) => RestResponse<ConsolidationBlueprintResult>;
  getActivationDates: (
    params: ConsolidationActivationDatesParams,
    signal?: AbortSignal
  ) => RestResponse<ConsolidationActivationDatesResult>;
  getTimelineWindowMetadata: (
    rootExternalId: string,
    options?: { timelineFallbackExternalId?: string },
    signal?: AbortSignal
  ) => RestResponse<ConsolidationTimelineWindowMetadataResult>;
  submitFeedback: (
    params: SubmitConsolidationFeedbackParams,
    signal?: AbortSignal
  ) => RestResponse<void>;
  getContainerChanges: (
    params: GetContainerChangeDetailParams,
    signal?: AbortSignal
  ) => RestResponse<ContainerChangeDetail[]>;
  getPriorities: (signal?: AbortSignal) => RestResponse<ConsolidationPriority[]>;
  getPriorityStatuses: (
    ids: string[],
    signal?: AbortSignal
  ) => RestResponse<ConsolidationPriorityStatusItem[]>;
  createPriority: (
    params: CreateConsolidationPriorityParams,
    signal?: AbortSignal
  ) => RestResponse<ConsolidationPriority>;
  updatePriority: (
    params: UpdateConsolidationPriorityParams,
    signal?: AbortSignal
  ) => RestResponse<void>;
  revokePriority: (id: string, signal?: AbortSignal) => RestResponse<void>;
};

/**
 * Raw window-metadata response. Inbound entries keep their backend (snake_case)
 * shape and are normalized by the consuming hook via parseInboundChange.
 */
export type ConsolidationTimelineWindowMetadataResult = {
  rootExternalId: string;
  windows: Array<{
    anchorLawExternalId: string;
    inboundChanges: Array<Record<string, unknown>>;
  }>;
};

export const buildConsolidationLawBasePath = (
  apiBasePath: string,
  lawExternalId: string
): string => `${apiBasePath}/laws/${encodeURIComponent(lawExternalId)}`;

export const appendConsolidationQuery = (
  url: string,
  query: Record<string, string | undefined>
): string => {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value.trim() !== "") {
      params.set(key, value);
    }
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
};

export const buildRenderUrl = (
  apiBasePath: string,
  params: ConsolidationRenderParams
): string =>
  appendConsolidationQuery(
    `${buildConsolidationLawBasePath(apiBasePath, params.lawExternalId)}/render`,
    {
      as_of: params.asOf,
      view: params.view ?? "draft",
    }
  );

export const buildRenderByChangeLawUrl = (
  apiBasePath: string,
  params: ConsolidationRenderByChangeLawParams
): string =>
  appendConsolidationQuery(
    `${buildConsolidationLawBasePath(apiBasePath, params.lawExternalId)}/render-by-change-law`,
    {
      change_law: params.changeLaw,
      as_of: params.asOf,
      view: params.view ?? "draft",
    }
  );

export const buildContainersUrl = (
  apiBasePath: string,
  params: ConsolidationContainersParams
): string =>
  appendConsolidationQuery(
    `${buildConsolidationLawBasePath(apiBasePath, params.lawExternalId)}/containers`,
    {
      as_of: params.asOf,
      view: params.view ?? "draft",
    }
  );

export const buildBlueprintUrl = (
  apiBasePath: string,
  lawExternalId: string
): string =>
  `${buildConsolidationLawBasePath(apiBasePath, lawExternalId)}/blueprint`;

export const buildActivationDatesUrl = (
  apiBasePath: string,
  params: ConsolidationActivationDatesParams
): string =>
  appendConsolidationQuery(
    `${buildConsolidationLawBasePath(apiBasePath, params.lawExternalId)}/activation-dates`,
    {
      change_law: params.changeLaw,
    }
  );

export const buildTimelineWindowMetadataUrl = (
  apiBasePath: string,
  rootExternalId: string,
  timelineFallbackExternalId?: string
): string => {
  const base = `${apiBasePath}/timelines/${encodeURIComponent(rootExternalId)}/window-metadata`;
  const fallback = timelineFallbackExternalId?.trim();
  if (!fallback || fallback === rootExternalId.trim()) {
    return base;
  }
  const params = new URLSearchParams({
    timeline_fallback: fallback,
  });
  return `${base}?${params.toString()}`;
};

export const buildPrioritiesUrl = (apiBasePath: string): string =>
  `${apiBasePath}/priorities`;

export const buildPriorityUrl = (apiBasePath: string, id: string): string =>
  `${buildPrioritiesUrl(apiBasePath)}/${encodeURIComponent(id)}`;

/** Mirrors the API's per-request id cap on GET priorities/status. */
export const PRIORITY_STATUS_BATCH_LIMIT = 50;

export const buildPriorityStatusUrl = (
  apiBasePath: string,
  ids: string[]
): string =>
  `${buildPrioritiesUrl(apiBasePath)}/status?ids=${encodeURIComponent(ids.join(","))}`;

export const buildFeedbackUrl = (
  apiBasePath: string,
  lawExternalId: string,
  containerId: string
): string =>
  `${buildConsolidationLawBasePath(apiBasePath, lawExternalId)}/containers/${encodeURIComponent(containerId)}/feedback`;

export const buildContainerChangesUrl = (
  apiBasePath: string,
  params: GetContainerChangeDetailParams
): string =>
  appendConsolidationQuery(
    `${buildConsolidationLawBasePath(apiBasePath, params.lawExternalId)}/containers/${encodeURIComponent(params.containerId)}/changes`,
    {
      as_of: params.asOf,
      view: params.view ?? "draft",
      change_law: params.changeLaw,
    }
  );

const consolidationService = (
  client: RestClient,
  path: string
): ConsolidationServiceType => ({
  render: async (params, signal) =>
    client.fetch<ConsolidationRenderResult>(
      RequestMethod.GET,
      buildRenderUrl(path, params),
      undefined,
      undefined,
      undefined,
      undefined,
      { signal }
    ),

  renderByChangeLaw: async (params, signal) =>
    client.fetch<ConsolidationRenderResult>(
      RequestMethod.GET,
      buildRenderByChangeLawUrl(path, params),
      undefined,
      undefined,
      undefined,
      undefined,
      { signal }
    ),

  getContainers: async (params, signal) =>
    client.fetch<ContainerRenderResult[]>(
      RequestMethod.GET,
      buildContainersUrl(path, params),
      undefined,
      undefined,
      undefined,
      undefined,
      { signal }
    ),

  getBlueprint: async (lawExternalId, signal) =>
    client.fetch<ConsolidationBlueprintResult>(
      RequestMethod.GET,
      buildBlueprintUrl(path, lawExternalId),
      undefined,
      undefined,
      undefined,
      undefined,
      { signal }
    ),

  getActivationDates: async (params, signal) =>
    client.fetch<ConsolidationActivationDatesResult>(
      RequestMethod.GET,
      buildActivationDatesUrl(path, params),
      undefined,
      undefined,
      undefined,
      undefined,
      { signal }
    ),

  getTimelineWindowMetadata: async (rootExternalId, options, signal) =>
    client.fetch<ConsolidationTimelineWindowMetadataResult>(
      RequestMethod.GET,
      buildTimelineWindowMetadataUrl(
        path,
        rootExternalId,
        options?.timelineFallbackExternalId
      ),
      undefined,
      undefined,
      undefined,
      undefined,
      { signal }
    ),

  submitFeedback: async (params, signal) =>
    client.fetch<void>(
      RequestMethod.POST,
      buildFeedbackUrl(path, params.lawExternalId, params.containerId),
      {
        message: params.message,
        category: params.category,
        asOf: params.asOf,
        view: params.view ?? DEFAULT_CONSOLIDATION_VIEW,
      },
      undefined,
      undefined,
      undefined,
      { signal }
    ),

  getContainerChanges: async (params, signal) =>
    client.fetch<ContainerChangeDetail[]>(
      RequestMethod.GET,
      buildContainerChangesUrl(path, params),
      undefined,
      undefined,
      undefined,
      undefined,
      { signal }
    ),

  getPriorities: async (signal) =>
    client.fetch<ConsolidationPriority[]>(
      RequestMethod.GET,
      buildPrioritiesUrl(path),
      undefined,
      undefined,
      undefined,
      undefined,
      { signal }
    ),

  createPriority: async (params, signal) =>
    client.fetch<ConsolidationPriority>(
      RequestMethod.POST,
      buildPrioritiesUrl(path),
      {
        lawExternalId: params.lawExternalId,
        pivotExternalId: params.pivotExternalId,
        mode: params.mode,
        level: params.level,
        lawTitle: params.lawTitle,
        pivotDocumentTypeId: params.pivotDocumentTypeId,
      },
      undefined,
      undefined,
      undefined,
      { signal }
    ),

  // The API caps the status endpoint at PRIORITY_STATUS_BATCH_LIMIT ids per
  // request, while the table's page sizes go beyond it — fetch in chunks and
  // merge, surfacing the first failed chunk as the overall failure.
  getPriorityStatuses: async (ids, signal) => {
    const chunks: string[][] = [];
    for (let i = 0; i < ids.length; i += PRIORITY_STATUS_BATCH_LIMIT) {
      chunks.push(ids.slice(i, i + PRIORITY_STATUS_BATCH_LIMIT));
    }
    if (chunks.length === 0) {
      return {
        success: true,
        status: 200,
        feedback: { severity: "success", message: "" },
        value: [],
      };
    }

    const responses = await Promise.all(
      chunks.map((chunk) =>
        client.fetch<ConsolidationPriorityStatusItem[]>(
          RequestMethod.GET,
          buildPriorityStatusUrl(path, chunk),
          undefined,
          undefined,
          undefined,
          undefined,
          { signal }
        )
      )
    );

    const failed = responses.find((response) => !response.success);
    if (failed) return failed;
    const succeeded = responses.filter((response) => response.success);
    return {
      ...succeeded[0],
      value: succeeded.flatMap((response) => response.value ?? []),
    };
  },

  updatePriority: async (params, signal) =>
    client.fetch<void>(
      RequestMethod.PATCH,
      buildPriorityUrl(path, params.id),
      {
        pivotExternalId: params.pivotExternalId,
        mode: params.mode,
        level: params.level,
        pivotDocumentTypeId: params.pivotDocumentTypeId,
      },
      undefined,
      undefined,
      undefined,
      { signal }
    ),

  revokePriority: async (id, signal) =>
    client.fetch<void>(
      RequestMethod.DELETE,
      buildPriorityUrl(path, id),
      undefined,
      undefined,
      undefined,
      undefined,
      { signal }
    ),
});

export default consolidationService;

export const DEFAULT_CONSOLIDATION_VIEW: ConsolidationView = "draft";
